/**
 * useWorkspaceSwitcher Hook
 * Manages workspace switching with proper API integration
 */

import { useCurrentWorkspace, useWorkspaceActions, useWorkspaces } from '@/store/slices'
import { useAuth } from '@/store/slices/auth-slice'
import { useCallback, useState } from 'react'
import { useGet, usePost } from './use-http'

/**
 * Workspace switching API response interface
 */
interface WorkspaceSwitchResponse {
  success: boolean
  message: string
  data: {
    workspace: any
    selectedWorkspace: any
    workspaces: any[]
    session: {
      sessionToken: string
      refreshToken?: string
      expires: string
      userId: string
    }
    user: any
  }
  error?: {
    message: string
    code: string
  }
}

/**
 * User workspaces API response interface
 */
interface WorkspaceListResponse {
  success: boolean
  data: {
    workspaces: any[]
    current: any
  }
  error?: {
    message: string
    code: string
  }
}

/**
 * Hook state interface
 */
interface UseWorkspaceSwitcherState {
  isSwitching: boolean
  isLoadingWorkspaces: boolean
  error: string | null
  lastSwitchedAt: number | null
}

/**
 * Hook return interface
 */
interface UseWorkspaceSwitcherReturn extends UseWorkspaceSwitcherState {
  switchWorkspace: (workspaceId: string) => Promise<void>
  refreshWorkspaces: () => Promise<void>
  clearError: () => void
  canSwitchTo: (workspaceId: string) => boolean
}

/**
 * Custom hook for workspace switching with API integration
 * Uses proper HTTP hooks and integrates with workspace store
 *
 * @returns {UseWorkspaceSwitcherReturn} Hook return object with switching functionality
 */
export function useWorkspaceSwitcher(): UseWorkspaceSwitcherReturn {
  const [state, setState] = useState<UseWorkspaceSwitcherState>({
    isSwitching: false,
    isLoadingWorkspaces: false,
    error: null,
    lastSwitchedAt: null
  })

  // Store hooks
  const { setCurrentWorkspace, setWorkspaces } = useWorkspaceActions()
  const currentWorkspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const { user } = useAuth()

  // API hooks
  const switchApi = usePost<WorkspaceSwitchResponse>('/workspaces/switch')
  const workspaceListApi = useGet<WorkspaceListResponse>('/workspaces/list', {
    immediate: false
  })

  /**
   * Clears the current error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  /**
   * Checks if user can switch to a specific workspace
   */
  const canSwitchTo = useCallback((workspaceId: string) => {

    if (!workspaces || workspaces.length === 0) {
      return false
    }
    if (currentWorkspace?.id === workspaceId) {
      return false
    }
    const canSwitch = workspaces.some(w => w.id === workspaceId)
    return canSwitch
  }, [workspaces, currentWorkspace])

  /**
   * Switches to a different workspace
   */
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    try {
      // Validate workspace switch
      if (!canSwitchTo(workspaceId)) {
        throw new Error('Cannot switch to the specified workspace')
      }

      setState(prev => ({
        ...prev,
        isSwitching: true,
        error: null
      }))

      // Make API call to switch workspace
      const response = await switchApi.execute({
        body: { workspaceId }
      })

      if (response.success && response.data) {
        // Update authentication tokens with new session data
        if (response.data.session) {
          const tokens = {
            accessToken: response.data.session.sessionToken,
            refreshToken: response.data.session.refreshToken || "",
            expiresAt: new Date(response.data.session.expires).getTime(),
          };

          // Import auth storage functions
          const { setAuthTokens } = await import('../utils/auth-storage');
          setAuthTokens(tokens);
        }

        // Update store with new workspace data
        setWorkspaces(response.data.workspaces)
        setCurrentWorkspace(response.data.selectedWorkspace)

        // Update localStorage for persistence (user-specific)
        // Store workspace preference for this specific user
        if (user?.email) {
          const userWorkspaceKey = `currentWorkspaceId_${user.email}`
          localStorage.setItem(userWorkspaceKey, workspaceId)
        }

        // Drop the cached app-id pointers — they belong to the *previous*
        // workspace, and after the reload the sidebar reads them blindly.
        // Without this, clicking Agents/Knowledge/Library navigates to
        // `/app/<old-app-id>/…` which the API rejects with 404 "App not
        // found" because that app belongs to the workspace we just left.
        // `useDefaultApp` will repopulate them for the new workspace on
        // the next render.
        try {
          localStorage.removeItem('taqat_default_app_id')
          localStorage.removeItem('taqat_last_active_app_id')
        } catch {
          // localStorage may be unavailable in restricted contexts.
        }

        // Fire custom event for other components to reload data
        window.dispatchEvent(new CustomEvent('workspace-changed', {
          detail: {
            workspaceId,
            workspace: response.data.selectedWorkspace,
            forceReload: true
          }
        }))

        // Navigate to /app so DefaultAppRedirect resolves the NEW workspace's default app.
        // A plain reload would re-request the previous workspace's appId from the URL and 404.
        window.location.href = '/app'

        setState(prev => ({
          ...prev,
          isSwitching: false,
          lastSwitchedAt: Date.now()
        }))

        return
      }

      throw new Error(response.error?.message || 'Failed to switch workspace')

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to switch workspace'

      setState(prev => ({
        ...prev,
        isSwitching: false,
        error: errorMessage
      }))

      // Re-throw for component handling
      throw error
    }
  }, [canSwitchTo, switchApi, setWorkspaces, setCurrentWorkspace])

  /**
   * Refreshes the workspace list from API
   */
  const refreshWorkspaces = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        isLoadingWorkspaces: true,
        error: null
      }))

      const response = await workspaceListApi.execute()

      if (response.success && response.data) {
        // Update store with fresh workspace data
        setWorkspaces(response.data.workspaces)

        // Update current workspace if it changed
        if (response.data.current) {
          setCurrentWorkspace(response.data.current)
        }

        setState(prev => ({
          ...prev,
          isLoadingWorkspaces: false
        }))

        return
      }

      throw new Error(response.error?.message || 'Failed to fetch workspaces')

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh workspaces'

      setState(prev => ({
        ...prev,
        isLoadingWorkspaces: false,
        error: errorMessage
      }))

      throw error
    }
  }, [workspaceListApi, setWorkspaces, setCurrentWorkspace])

  return {
    // State
    isSwitching: state.isSwitching || switchApi.loading,
    isLoadingWorkspaces: state.isLoadingWorkspaces || workspaceListApi.loading,
    error: state.error || switchApi.error?.message || workspaceListApi.error?.message || null,
    lastSwitchedAt: state.lastSwitchedAt,

    // Actions
    switchWorkspace,
    refreshWorkspaces,
    clearError,
    canSwitchTo
  }
}

/**
 * Hook variant that auto-refreshes workspaces on mount
 * Use this when you need workspace list to be always up to date
 */

/**
 * Utility function to format workspace switch error messages
 */
